import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Edit3,
  Loader2,
  PlayCircle,
  RefreshCw,
  X,
} from 'lucide-react'

import { api, ApiError } from '@/lib/api'
import { waitForPhaseCompletion } from '@/lib/phaseEvents'
import { decisionLabel } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'
import type { GateDecisionRecord, GateDecisionStatus, Project } from '@/types'
import { DownloadCenter } from '../downloads/DownloadCenter'
import {
  gateDraftKey,
  reconcileDraftWithGate,
  useGateDraft,
  useGateDraftStore,
} from '@/stores/gateDraftStore'
import { AutoApproveAllButton } from '@/components/command-center/AutoApproveAllButton'

export interface ContentAwareG5Props {
  project: Project
  runId: number
  onApproved?: () => void
}

export function ContentAwareG5Gate({ project, runId, onApproved }: ContentAwareG5Props) {
  const queryClient = useQueryClient()
  const gateNumber = 5
  const draftKey = gateDraftKey(project.id, runId, gateNumber)
  const decisions = useGateDraft(draftKey)
  const setDecisionsDraft = useGateDraftStore((s) => s.setDraft)
  const clearDecisionsDraft = useGateDraftStore((s) => s.clearDraft)
  const setDecisions = (next: GateDecisionRecord[]) =>
    setDecisionsDraft(draftKey, next)

  const gate = useQuery({
    queryKey: ['gate', project.id, runId, gateNumber],
    queryFn: () => api.getGate(project.id, runId, gateNumber),
    retry: false,
  })

  // Share the artifact-index cache with the parent so we can tell whether
  // Phase 5 has actually produced an export bundle (plan §P0.5). Without
  // this guard the user could approve G5 against a 404'd export — every
  // download tile would silently return an HTML 404 and the "finalized"
  // state would be a lie.
  const artifactIndex = useQuery<{ files: { name: string }[] }>({
    queryKey: ['artifact-index', project.id, runId],
    queryFn: async () => {
      const res = await fetch(
        `/api/projects/${encodeURIComponent(project.id)}/runs/${runId}/artifacts`,
      )
      if (!res.ok) throw new Error(`index fetch failed: ${res.status}`)
      return (await res.json()) as { files: { name: string }[] }
    },
    retry: false,
  })
  const phase5Done = (artifactIndex.data?.files ?? []).some(
    (f) => f.name === 'requirements.md' || f.name === 'export.zip',
  )

  // Kick Phase 5 directly from the gate when the architect lands here
  // before the Exporter has run. Mirrors the embedded DownloadCenter's
  // Run Exporter button so the user has one clear path forward without
  // hunting for the right button.
  const runPhase5 = useMutation({
    mutationFn: async () => {
      await api.runPhase5(project.id, runId)
      return waitForPhaseCompletion(project.id, runId, 5)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artifact-index', project.id, runId] })
      queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      toast({ variant: 'success', title: 'Phase 5 complete', description: 'Export bundle is ready — finalize below.' })
    },
    onError: (err) => toast({ variant: 'destructive', title: 'Phase 5 failed', description: (err as Error).message }),
  })

  // Seed decisions from the freshly-fetched gate payload, reconciling
  // any persisted localStorage draft against the server's decision-id
  // set first. See ``reconcileDraftWithGate`` for the stale-draft
  // hygiene that keeps a deleted-and-recreated project from inheriting
  // the prior project's Approve/Edit/Reject picks.
  useEffect(() => {
    if (!gate.data) return
    reconcileDraftWithGate(draftKey, gate.data)
  }, [gate.data, draftKey])

  const emit = useMutation({
    mutationFn: () => api.createGate(project.id, runId, gateNumber),
    onSuccess: () => {
      clearDecisionsDraft(draftKey)
      gate.refetch()
      toast({ variant: 'success', title: 'G5 re-emitted' })
    },
    onError: (err) => toast({ variant: 'destructive', title: 'Re-emit failed', description: (err as Error).message }),
  })

  const submit = useMutation({
    mutationFn: () => api.submitGateDecisions(project.id, runId, gateNumber, decisions),
    onSuccess: (g) => {
      gate.refetch()
      if (g.status === 'approved') {
        toast({ variant: 'success', title: 'G5 approved', description: 'Export is finalized.' })
        onApproved?.()
        queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      }
    },
    onError: (err) => toast({ variant: 'destructive', title: 'Could not submit decisions', description: (err as Error).message }),
  })

  const allDecided = decisions.length > 0 && decisions.every((d) => d.status !== 'pending')

  const updateDecision = (id: string, patch: Partial<GateDecisionRecord>) =>
    setDecisions(decisions.map((d) => (d.id === id ? { ...d, ...patch } : d)))

  const missing = gate.error instanceof ApiError && gate.error.status === 404

  return (
    <div className="alm-fadein">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="alm-chip alm-chip-warn"><AlertCircle className="h-3 w-3" /> Needs you</span>
            <span className="alm-chip">Gate G5</span>
            {decisions.length > 0 && <span className="alm-chip alm-chip-info">{decisions.length} decisions</span>}
            {gate.data?.status && <span className="alm-chip">{gate.data.status}</span>}
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-100">Final Export Review</h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Confirm the final requirements bundle before delivery. Resolve any template mis-fits below.
          </p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <button type="button" onClick={() => emit.mutate()} disabled={emit.isPending} className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-50">
            {emit.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Re-emit
          </button>
        </div>
      </div>

      {missing && (
        <div className="rounded-md border border-slate-700/60 bg-slate-900/60 p-4 mb-5 text-sm text-slate-300">
          <p className="font-semibold">No gate yet</p>
          <p className="text-xs text-slate-400 mt-1">Click <strong>Re-emit</strong> above to build the G5 artifact.</p>
        </div>
      )}

      {gate.isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-5">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading gate…
        </div>
      )}

      {!missing && !phase5Done && !artifactIndex.isLoading && (
        <div className="rounded-lg border border-amber-700/50 bg-amber-950/30 p-4 mb-5 text-sm text-amber-100">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-amber-300" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold">Run Phase 5 first</p>
              <p className="text-xs text-amber-200/80 mt-1 leading-relaxed">
                The Exporter hasn't produced a bundle yet, so the download tiles below
                would 404 and finalizing this gate would record approval against an
                export that doesn't exist. Run Phase 5 to assemble the corporate
                package, then come back here to finalize.
              </p>
              <button
                type="button"
                onClick={() => runPhase5.mutate()}
                disabled={runPhase5.isPending}
                className="mt-3 inline-flex items-center gap-2 rounded-md bg-gradient-to-b from-amber-500 to-amber-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-amber-500/20 disabled:opacity-50 hover:from-amber-400 hover:to-amber-500"
              >
                {runPhase5.isPending ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Running Phase 5…</>
                ) : (
                  <><PlayCircle className="h-3.5 w-3.5" /> Run Phase 5</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {!missing && (
        <>
          <div className="space-y-3 mb-6">
            {decisions.map((decision) => {
              const isMisfit = decision.id.startsWith('G5-MF-') || decision.id.startsWith('G5-UT-')
              
              // Map decision status to the testSuffix used by
              // check-testids.mjs (plan W4.2 / §5.5). Includes ``skipped``
              // (Phase 0 skip flow) so the record covers every member of
              // ``GateDecisionStatus``.
              const STATUS_SUFFIX: Record<GateDecisionStatus, 'approve' | 'modify' | 'reject' | 'pending' | 'skipped'> = {
                approved: 'approve',
                edited: 'modify',
                rejected: 'reject',
                pending: 'pending',
                skipped: 'skipped',
              }
              return (
                <div
                  key={decision.id}
                  className={`alm-card ${isMisfit ? 'border-amber-700/50 bg-amber-950/20' : ''}`}
                  data-testid={`gate-${gateNumber}-row`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className={`text-[10.5px] ${isMisfit ? 'text-amber-500' : 'text-slate-500'}`}>{decision.id}</div>
                      <div className="text-sm text-slate-100 mt-0.5 leading-relaxed">{decision.question}</div>
                    </div>
                    <div className="flex gap-1.5 shrink-0 flex-wrap">
                      {(['approved', 'edited', 'rejected'] as GateDecisionStatus[]).map((status) => {
                        const testSuffix = STATUS_SUFFIX[status]
                        return (
                          <button
                            key={status}
                            type="button"
                            data-testid={`gate-${gateNumber}-${testSuffix}`}
                            onClick={() => updateDecision(decision.id, { status })}
                            className="alm-decision"
                            data-selected={decision.status === status ? status : undefined}
                          >
                            {status === 'approved' ? <Check className="h-3 w-3" /> : status === 'edited' ? <Edit3 className="h-3 w-3" /> : <X className="h-3 w-3" />}
                            <span>{decisionLabel(status)}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                  {decision.status === 'edited' && (
                    <textarea
                      data-testid={`gate-${gateNumber}-edit-text`}
                      value={decision.answer ?? ''}
                      onChange={(e) => updateDecision(decision.id, { answer: e.target.value })}
                      rows={2}
                      placeholder={isMisfit ? "Describe how you'd like to handle this mis-fit..." : "Describe your edits..."}
                      className="w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 mt-2"
                    />
                  )}
                  {decision.status === 'rejected' && (
                    <textarea
                      data-testid={`gate-${gateNumber}-rationale`}
                      value={decision.rejection_reason ?? ''}
                      onChange={(e) => updateDecision(decision.id, { rejection_reason: e.target.value })}
                      rows={2}
                      placeholder="Why are you rejecting this?"
                      className="w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 mt-2"
                    />
                  )}
                </div>
              )
            })}
          </div>

          <div className="mb-6">
             <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-3">Export Bundle Preview</div>
             <DownloadCenter projectId={project.id} runId={runId} />
          </div>

          {gate.data?.status === 'approved' && (
            <div className="rounded-lg p-4 mb-5 border border-emerald-700/40 bg-emerald-950/20 flex items-center gap-2 text-emerald-300 text-sm">
              <CheckCircle2 className="h-4 w-4" /> G5 approved — export complete
            </div>
          )}

          {/* Primary actions */}
          <div className="flex flex-wrap items-center gap-2">
            {gate.data?.status !== 'approved' && (
              <>
                {/* Plan §P1.12 — bulk approve, but skip mis-fit /
                    unbound-template rows because those genuinely need
                    the architect to decide whether to ship the gap or
                    rewind. */}
                <AutoApproveAllButton
                  decisions={decisions}
                  setDecisions={setDecisions}
                  gateName="G5"
                  disabled={submit.isPending}
                  skip={(d) =>
                    d.id.startsWith('G5-MF-') || d.id.startsWith('G5-UT-')
                  }
                />
                <button
                  type="button"
                  data-testid={`gate-${gateNumber}-submit`}
                  onClick={() => submit.mutate()}
                  disabled={!allDecided || submit.isPending || !phase5Done}
                  title={
                    !phase5Done
                      ? 'Run Phase 5 first — the export bundle has not been produced yet.'
                      : !allDecided
                      ? 'Choose Approve / Edit / Reject for every decision first.'
                      : undefined
                  }
                  className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-400 hover:to-indigo-500"
                >
                  {submit.isPending ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <><Check className="h-4 w-4" /> Finalize Export</>}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}
